import React, { useEffect, useRef, useState } from 'react';
import {
  getProfitCentres, createProfitCentre, updateProfitCentre, deleteProfitCentre,
  getCategories, createCategory, updateCategory, deleteCategory,
  getBillingEntities, createBillingEntity, updateBillingEntity, deleteBillingEntity,
  getAllExpenseCategories, createExpenseCategory, updateExpenseCategory, deleteExpenseCategory,
  getComplianceTypes, createComplianceType, updateComplianceType, deleteComplianceType,
  createSignatory, updateSignatory, deleteSignatory,
  uploadsUrl,
} from '../api';

interface MasterItem { id: number; name: string; isActive?: boolean; }

interface CategoryItem { id: number; name: string; hsnSacCode?: string; }

interface SignatoryItem {
  id: number;
  billingEntityId: number;
  name: string;
  designation?: string;
  signatureFile?: string;
  isDefault: boolean;
}

interface BillingEntityItem {
  id: number;
  name: string;
  gstin?: string;
  pan?: string;
  address?: string;
  city?: string;
  state?: string;
  stateCode?: string;
  email?: string;
  phone?: string;
  emailSenderName?: string;
  bankName?: string;
  bankAccount?: string;
  bankIfsc?: string;
  bankBranch?: string;
  signatories?: SignatoryItem[];
}

type MasterType = 'profitCentre' | 'expenseCategory';

interface MasterSection {
  type: MasterType;
  label: string;
  description: string;
  items: MasterItem[];
}

const MasterTable: React.FC<{
  section: MasterSection;
  onAdd: (type: MasterType, name: string) => Promise<void>;
  onEdit: (type: MasterType, id: number, name: string) => Promise<void>;
  onDelete: (type: MasterType, id: number) => Promise<void>;
}> = ({ section, onAdd, onEdit, onDelete }) => {
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setError(''); setSaving(true);
    try {
      await onAdd(section.type, newName.trim());
      setNewName('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error adding item');
    } finally { setSaving(false); }
  };

  const startEdit = (item: MasterItem) => { setEditId(item.id); setEditName(item.name); setError(''); };

  const handleEdit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!editName.trim() || editId === null) return;
    setError(''); setSaving(true);
    try {
      await onEdit(section.type, editId, editName.trim());
      setEditId(null); setEditName('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error updating item');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this item? It cannot be deleted if used by any task.')) return;
    setError('');
    try { await onDelete(section.type, id); }
    catch (err: any) { setError(err.response?.data?.message || 'Error deleting item'); }
  };

  return (
    <div className="card">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-900">{section.label}</h3>
        <p className="text-xs text-gray-500 mt-0.5">{section.description}</p>
      </div>

      {/* Add form */}
      <form onSubmit={handleAdd} className="flex gap-2 mb-4">
        <input
          className="input-field flex-1"
          placeholder={`Add new ${section.label.toLowerCase()}...`}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <button type="submit" className="btn-primary text-sm whitespace-nowrap" disabled={saving}>
          + Add
        </button>
      </form>

      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

      {section.items.length === 0 ? (
        <p className="text-gray-400 text-sm">No items yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="table-header">Name</th>
              <th className="table-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {section.items.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="table-cell w-full">
                  {editId === item.id ? (
                    <input
                      className="input-field w-full"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleEdit(); if (e.key === 'Escape') setEditId(null); }}
                      autoFocus
                    />
                  ) : (
                    <span className="font-medium text-gray-800">{item.name}</span>
                  )}
                </td>
                <td className="table-cell whitespace-nowrap">
                  {editId === item.id ? (
                    <div className="flex gap-2">
                      <button className="btn-primary text-xs px-3 py-1" onClick={() => handleEdit()} disabled={saving}>Save</button>
                      <button className="btn-secondary text-xs px-3 py-1" onClick={() => setEditId(null)}>Cancel</button>
                    </div>
                  ) : (
                    <div className="flex gap-3">
                      <button className="text-blue-600 hover:text-blue-800 text-xs font-medium" onClick={() => startEdit(item)}>Edit</button>
                      <button className="text-red-600 hover:text-red-800 text-xs font-medium" onClick={() => handleDelete(item.id)}>Delete</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ─── Category Table (with HSN/SAC) ─────────────────────────────────────────

const EMPTY_CAT = { name: '', hsnSacCode: '' };

const CategoryTable: React.FC<{ items: CategoryItem[]; onRefresh: () => void }> = ({ items, onRefresh }) => {
  const [editId, setEditId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_CAT);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const resetForm = () => { setForm(EMPTY_CAT); setEditId(null); setShowForm(false); setError(''); };

  const startEdit = (cat: CategoryItem) => {
    setEditId(cat.id);
    setForm({ name: cat.name, hsnSacCode: cat.hsnSacCode || '' });
    setShowForm(true); setError('');
  };

  const f = (k: keyof typeof EMPTY_CAT) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true); setError('');
    try {
      const data = { name: form.name.trim(), hsnSacCode: form.hsnSacCode.trim() || null };
      if (editId) await updateCategory(editId, data);
      else await createCategory(data);
      resetForm(); onRefresh();
    } catch (err: any) { setError(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this category? Cannot delete if used by any task.')) return;
    setError('');
    try { await deleteCategory(id); onRefresh(); }
    catch (err: any) { setError(err.response?.data?.message || 'Error'); }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Task Categories</h3>
          <p className="text-xs text-gray-500 mt-0.5">Task categories with optional HSN/SAC code for invoicing</p>
        </div>
        <button className="btn-primary text-sm" onClick={() => { resetForm(); setShowForm(true); }}>+ Add</button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-3 mb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Name <span className="text-red-500">*</span></label>
              <input className="input-field" value={form.name} onChange={f('name')} required />
            </div>
            <div>
              <label className="label">HSN / SAC Code</label>
              <input className="input-field font-mono" value={form.hsnSacCode} onChange={f('hsnSacCode')} placeholder="e.g. 998231" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" className="btn-secondary text-sm" onClick={resetForm}>Cancel</button>
            <button type="submit" className="btn-primary text-sm" disabled={saving}>{editId ? 'Update' : 'Create'}</button>
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
        </form>
      )}

      {!showForm && error && <p className="text-red-600 text-sm mb-3">{error}</p>}

      {items.length === 0 ? (
        <p className="text-gray-400 text-sm">No categories yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="table-header">Name</th>
              <th className="table-header">HSN / SAC</th>
              <th className="table-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((cat) => (
              <tr key={cat.id} className="hover:bg-gray-50">
                <td className="table-cell font-medium text-gray-800">{cat.name}</td>
                <td className="table-cell text-xs font-mono text-gray-500">{cat.hsnSacCode || <span className="text-gray-300">—</span>}</td>
                <td className="table-cell whitespace-nowrap">
                  <div className="flex gap-3">
                    <button className="text-blue-600 hover:text-blue-800 text-xs font-medium" onClick={() => startEdit(cat)}>Edit</button>
                    <button className="text-red-600 hover:text-red-800 text-xs font-medium" onClick={() => handleDelete(cat.id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ─── Signatory Panel (modal per billing entity) ────────────────────────────

const EMPTY_SIG = { name: '', designation: '', isDefault: false };

const SignatoryPanel: React.FC<{
  billingEntity: BillingEntityItem;
  onClose: () => void;
  onRefresh: () => void;
}> = ({ billingEntity, onClose, onRefresh }) => {
  const [signatories, setSignatories] = useState<SignatoryItem[]>(billingEntity.signatories ?? []);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_SIG);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setForm(EMPTY_SIG); setFile(null); setPreview(null);
    setEditId(null); setShowForm(false); setError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const startEdit = (s: SignatoryItem) => {
    setEditId(s.id);
    setForm({ name: s.name, designation: s.designation ?? '', isDefault: s.isDefault });
    setFile(null);
    setPreview(s.signatureFile ? uploadsUrl(s.signatureFile) : null);
    setShowForm(true); setError('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    if (f) setPreview(URL.createObjectURL(f));
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this signatory?')) return;
    setError('');
    try {
      await deleteSignatory(id);
      setSignatories(prev => prev.filter(s => s.id !== id));
      onRefresh();
    } catch (err: any) { setError(err.response?.data?.message || 'Error deleting'); }
  };

  const handleSubmitWithRefresh = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true); setError('');
    try {
      const fd = new FormData();
      fd.append('billingEntityId', String(billingEntity.id));
      fd.append('name', form.name.trim());
      fd.append('designation', form.designation);
      fd.append('isDefault', String(form.isDefault));
      if (file) fd.append('signatureFile', file);

      if (editId) await updateSignatory(editId, fd);
      else await createSignatory(fd);

      resetForm();
      onRefresh();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error saving signatory');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Signatories — {billingEntity.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">Manage authorised signatories for this billing entity</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {error && <p className="text-red-600 text-sm">{error}</p>}

          {/* Add / Edit form */}
          {showForm ? (
            <form onSubmit={handleSubmitWithRefresh} className="bg-gray-50 rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium text-gray-700">{editId ? 'Edit Signatory' : 'Add Signatory'}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Name <span className="text-red-500">*</span></label>
                  <input className="input-field" value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required />
                </div>
                <div>
                  <label className="label">Designation</label>
                  <input className="input-field" value={form.designation}
                    onChange={e => setForm(p => ({ ...p, designation: e.target.value }))}
                    placeholder="e.g. Partner, Director" />
                </div>
              </div>
              <div>
                <label className="label">Signature Image (PNG/JPG)</label>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                {preview && (
                  <div className="mt-2">
                    <img src={preview} alt="Signature preview" className="h-16 object-contain border border-gray-200 rounded p-1 bg-white" />
                  </div>
                )}
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.isDefault}
                    onChange={e => setForm(p => ({ ...p, isDefault: e.target.checked }))}
                    className="w-4 h-4 accent-blue-600" />
                  <span className="text-sm text-gray-700">Set as default signatory for this entity</span>
                </label>
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" className="btn-secondary text-sm" onClick={resetForm}>Cancel</button>
                <button type="submit" className="btn-primary text-sm" disabled={saving}>
                  {saving ? 'Saving…' : editId ? 'Update' : 'Add Signatory'}
                </button>
              </div>
            </form>
          ) : (
            <button className="btn-primary text-sm" onClick={() => { resetForm(); setShowForm(true); }}>
              + Add Signatory
            </button>
          )}

          {/* Signatory list */}
          {billingEntity.signatories && billingEntity.signatories.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="table-header">Signature</th>
                  <th className="table-header">Name</th>
                  <th className="table-header">Designation</th>
                  <th className="table-header">Default</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {billingEntity.signatories.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="table-cell w-20">
                      {s.signatureFile
                        ? <img src={uploadsUrl(s.signatureFile)} alt="sig" className="h-10 object-contain" />
                        : <span className="text-gray-300 text-xs">No image</span>
                      }
                    </td>
                    <td className="table-cell font-medium text-gray-800">{s.name}</td>
                    <td className="table-cell text-xs text-gray-500">{s.designation || '—'}</td>
                    <td className="table-cell text-center">
                      {s.isDefault && <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">Default</span>}
                    </td>
                    <td className="table-cell whitespace-nowrap">
                      <div className="flex gap-3">
                        <button className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                          onClick={() => startEdit(s)}>Edit</button>
                        <button className="text-red-600 hover:text-red-800 text-xs font-medium"
                          onClick={() => handleDelete(s.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            !showForm && <p className="text-gray-400 text-sm">No signatories added yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Billing Entities Table ────────────────────────────────────────────────

const EMPTY_BE = { name: '', gstin: '', pan: '', address: '', city: '', state: '', stateCode: '', email: '', phone: '', emailSenderName: '', bankName: '', bankAccount: '', bankIfsc: '', bankBranch: '' };

const BillingEntityTable: React.FC<{ items: BillingEntityItem[]; onRefresh: () => void }> = ({ items, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_BE);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [signatoryEntityId, setSignatoryEntityId] = useState<number | null>(null);
  const signatoryEntity = signatoryEntityId ? (items.find(b => b.id === signatoryEntityId) ?? null) : null;

  const resetForm = () => { setForm(EMPTY_BE); setEditId(null); setShowForm(false); setError(''); };

  const startEdit = (be: BillingEntityItem) => {
    setEditId(be.id);
    setForm({
      name: be.name, gstin: be.gstin || '', pan: be.pan || '',
      address: be.address || '', city: be.city || '', state: be.state || '', stateCode: be.stateCode || '',
      email: be.email || '', phone: be.phone || '', emailSenderName: be.emailSenderName || '',
      bankName: be.bankName || '', bankAccount: be.bankAccount || '', bankIfsc: be.bankIfsc || '', bankBranch: be.bankBranch || '',
    });
    setShowForm(true); setError('');
  };

  const f = (k: keyof typeof EMPTY_BE) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true); setError('');
    try {
      const data: any = {};
      Object.entries(form).forEach(([k, v]) => { data[k] = v.trim() || null; });
      data.name = form.name.trim();
      if (editId) await updateBillingEntity(editId, data);
      else await createBillingEntity(data);
      resetForm(); onRefresh();
    } catch (err: any) { setError(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this billing entity? Cannot delete if used by any invoice or task.')) return;
    setError('');
    try { await deleteBillingEntity(id); onRefresh(); }
    catch (err: any) { setError(err.response?.data?.message || 'Error'); }
  };

  return (
    <div className="card col-span-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Billing Entities</h3>
          <p className="text-xs text-gray-500 mt-0.5">Entities under which invoices are raised — GSTIN, address, bank details</p>
        </div>
        <button className="btn-primary text-sm" onClick={() => { resetForm(); setShowForm(true); }}>+ Add</button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-4 mb-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="col-span-2">
              <label className="label">Name <span className="text-red-500">*</span></label>
              <input className="input-field" value={form.name} onChange={f('name')} required />
            </div>
            <div>
              <label className="label">GSTIN</label>
              <input className="input-field" value={form.gstin} onChange={f('gstin')} placeholder="22AAAAA0000A1Z5" />
            </div>
            <div>
              <label className="label">PAN</label>
              <input className="input-field uppercase" value={form.pan} onChange={f('pan')} placeholder="ABCDE1234F" maxLength={10} />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="col-span-2">
              <label className="label">Address</label>
              <input className="input-field" value={form.address} onChange={f('address')} />
            </div>
            <div>
              <label className="label">City</label>
              <input className="input-field" value={form.city} onChange={f('city')} />
            </div>
            <div>
              <label className="label">State</label>
              <input className="input-field" value={form.state} onChange={f('state')} />
            </div>
            <div>
              <label className="label">State Code</label>
              <input className="input-field" value={form.stateCode} onChange={f('stateCode')} placeholder="e.g. 07" maxLength={2} />
            </div>
            <div>
              <label className="label">Email</label>
              <input type="email" className="input-field" value={form.email} onChange={f('email')} />
            </div>
            <div>
              <label className="label">Phone</label>
              <input className="input-field" value={form.phone} onChange={f('phone')} />
            </div>
            <div className="col-span-2">
              <label className="label">Email Sender Name</label>
              <input className="input-field" value={form.emailSenderName} onChange={f('emailSenderName')}
                placeholder="e.g. GSTconnect Office — Accounts" />
              <p className="text-xs text-gray-400 mt-1">Shown as the signature on invoices/receipts/reminders raised under this entity. Leave blank to use the org-wide default (Settings → Billing Emails).</p>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Bank Details</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="label">Bank Name</label>
                <input className="input-field" value={form.bankName} onChange={f('bankName')} />
              </div>
              <div>
                <label className="label">Account No.</label>
                <input className="input-field" value={form.bankAccount} onChange={f('bankAccount')} />
              </div>
              <div>
                <label className="label">IFSC</label>
                <input className="input-field" value={form.bankIfsc} onChange={f('bankIfsc')} />
              </div>
              <div>
                <label className="label">Branch</label>
                <input className="input-field" value={form.bankBranch} onChange={f('bankBranch')} />
              </div>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" className="btn-secondary text-sm" onClick={resetForm}>Cancel</button>
            <button type="submit" className="btn-primary text-sm" disabled={saving}>{editId ? 'Update' : 'Create'}</button>
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
        </form>
      )}

      {!showForm && error && <p className="text-red-600 text-sm mb-3">{error}</p>}

      {items.length === 0 ? (
        <p className="text-gray-400 text-sm">No billing entities yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="table-header">Name</th>
              <th className="table-header">GSTIN</th>
              <th className="table-header">PAN</th>
              <th className="table-header">Address</th>
              <th className="table-header">Bank</th>
              <th className="table-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((be) => (
              <tr key={be.id} className="hover:bg-gray-50">
                <td className="table-cell font-medium text-gray-800 whitespace-nowrap">{be.name}</td>
                <td className="table-cell text-xs font-mono">{be.gstin || <span className="text-gray-300">—</span>}</td>
                <td className="table-cell text-xs font-mono">{be.pan || <span className="text-gray-300">—</span>}</td>
                <td className="table-cell text-xs text-gray-500">
                  {[be.address, be.city, be.state].filter(Boolean).join(', ') || <span className="text-gray-300">—</span>}
                </td>
                <td className="table-cell text-xs text-gray-500">
                  {be.bankName ? <span>{be.bankName}{be.bankIfsc ? ` · ${be.bankIfsc}` : ''}</span> : <span className="text-gray-300">—</span>}
                </td>
                <td className="table-cell whitespace-nowrap">
                  <div className="flex gap-3">
                    <button className="text-blue-600 hover:text-blue-800 text-xs font-medium" onClick={() => startEdit(be)}>Edit</button>
                    <button className="text-purple-600 hover:text-purple-800 text-xs font-medium" onClick={() => setSignatoryEntityId(be.id)}>
                      ✍️ Signatories {be.signatories && be.signatories.length > 0 ? `(${be.signatories.length})` : ''}
                    </button>
                    <button className="text-red-600 hover:text-red-800 text-xs font-medium" onClick={() => handleDelete(be.id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {signatoryEntity && (
        <SignatoryPanel
          billingEntity={signatoryEntity}
          onClose={() => setSignatoryEntityId(null)}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
};

// ─── Compliance Types Table ─────────────────────────────────────────────────

interface CompType { id: number; name: string; frequency: string; dueDay: number; dueMonthOffset: number; description?: string; isActive: boolean; _count?: { compliances: number; taskComplianceTypes: number }; }

const ComplianceTypeTable: React.FC<{ items: CompType[]; onRefresh: () => void }> = ({ items, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ name: '', frequency: 'MONTHLY', dueDay: '20', dueMonthOffset: '1', description: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const resetForm = () => { setForm({ name: '', frequency: 'MONTHLY', dueDay: '20', dueMonthOffset: '1', description: '' }); setEditId(null); setShowForm(false); setError(''); };

  const startEdit = (ct: CompType) => {
    setEditId(ct.id);
    setForm({ name: ct.name, frequency: ct.frequency, dueDay: String(ct.dueDay), dueMonthOffset: String(ct.dueMonthOffset), description: ct.description || '' });
    setShowForm(true); setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true); setError('');
    try {
      const data = { name: form.name.trim(), frequency: form.frequency, dueDay: Number(form.dueDay), dueMonthOffset: Number(form.dueMonthOffset), description: form.description || null };
      if (editId) await updateComplianceType(editId, data);
      else await createComplianceType(data);
      resetForm(); onRefresh();
    } catch (err: any) { setError(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this compliance type?')) return;
    setError('');
    try { await deleteComplianceType(id); onRefresh(); }
    catch (err: any) { setError(err.response?.data?.message || 'Error'); }
  };

  return (
    <div className="card col-span-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Compliance Types</h3>
          <p className="text-xs text-gray-500 mt-0.5">Define recurring compliance types with frequency and due-date rules</p>
        </div>
        <button className="btn-primary text-sm" onClick={() => { resetForm(); setShowForm(true); }}>+ Add</button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="col-span-2">
              <label className="label">Name <span className="text-red-500">*</span></label>
              <input className="input-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div>
              <label className="label">Frequency</label>
              <select className="input-field" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
                <option value="MONTHLY">Monthly</option>
                <option value="QUARTERLY">Quarterly</option>
                <option value="HALF_YEARLY">Half-Yearly</option>
                <option value="ANNUAL">Annual</option>
              </select>
            </div>
            <div>
              <label className="label">Due Day of Month</label>
              <input type="number" min="1" max="31" className="input-field" value={form.dueDay} onChange={(e) => setForm({ ...form, dueDay: e.target.value })} />
            </div>
            <div>
              <label className="label">Due Months After Period</label>
              <input type="number" min="0" max="12" className="input-field" value={form.dueMonthOffset} onChange={(e) => setForm({ ...form, dueMonthOffset: e.target.value })} />
            </div>
            <div className="col-span-2 md:col-span-3">
              <label className="label">Description</label>
              <input className="input-field" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional description" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" className="btn-secondary text-sm" onClick={resetForm}>Cancel</button>
            <button type="submit" className="btn-primary text-sm" disabled={saving}>{editId ? 'Update' : 'Create'}</button>
          </div>
        </form>
      )}

      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

      {items.length === 0 ? (
        <p className="text-gray-400 text-sm">No compliance types yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="table-header">Name</th>
              <th className="table-header">Frequency</th>
              <th className="table-header">Due Day</th>
              <th className="table-header">Month Offset</th>
              <th className="table-header">Description</th>
              <th className="table-header">Enrolled</th>
              <th className="table-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((ct) => (
              <tr key={ct.id} className="hover:bg-gray-50">
                <td className="table-cell font-medium text-gray-800">{ct.name}</td>
                <td className="table-cell text-xs">{ct.frequency.replace(/_/g, ' ')}</td>
                <td className="table-cell text-xs text-center">{ct.dueDay}</td>
                <td className="table-cell text-xs text-center">{ct.dueMonthOffset}</td>
                <td className="table-cell text-xs text-gray-500">{ct.description || '—'}</td>
                <td className="table-cell text-xs text-center">{ct._count?.taskComplianceTypes ?? 0} tasks</td>
                <td className="table-cell whitespace-nowrap">
                  <div className="flex gap-3">
                    <button className="text-blue-600 hover:text-blue-800 text-xs font-medium" onClick={() => startEdit(ct)}>Edit</button>
                    <button className="text-red-600 hover:text-red-800 text-xs font-medium" onClick={() => handleDelete(ct.id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ─── Masters Page ──────────────────────────────────────────────────────────────

const Masters: React.FC = () => {
  const [profitCentres, setProfitCentres] = useState<MasterItem[]>([]);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [billingEntities, setBillingEntities] = useState<BillingEntityItem[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<MasterItem[]>([]);
  const [complianceTypesData, setComplianceTypesData] = useState<CompType[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    try {
      const [pcRes, catRes, beRes, ecRes, ctRes] = await Promise.all([
        getProfitCentres(), getCategories(), getBillingEntities(), getAllExpenseCategories(), getComplianceTypes(),
      ]);
      setProfitCentres(pcRes.data);
      setCategories(catRes.data);
      setBillingEntities(beRes.data);
      setExpenseCategories(ecRes.data);
      setComplianceTypesData(ctRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleAdd = async (type: MasterType, name: string) => {
    if (type === 'profitCentre') await createProfitCentre({ name });
    else await createExpenseCategory({ name });
    await fetchAll();
  };

  const handleEdit = async (type: MasterType, id: number, name: string) => {
    if (type === 'profitCentre') await updateProfitCentre(id, { name });
    else await updateExpenseCategory(id, { name });
    await fetchAll();
  };

  const handleDelete = async (type: MasterType, id: number) => {
    if (type === 'profitCentre') await deleteProfitCentre(id);
    else await deleteExpenseCategory(id);
    await fetchAll();
  };

  const sections: MasterSection[] = [
    {
      type: 'profitCentre',
      label: 'Profit Centres',
      description: 'Profit centres for grouping and reporting tasks',
      items: profitCentres,
    },
    {
      type: 'expenseCategory',
      label: 'Expense Categories',
      description: 'Categories for reimbursement line items (e.g. Travel, Courier)',
      items: expenseCategories,
    },
  ];

  const [activeTab, setActiveTab] = useState<'masters' | 'complianceTypes'>('masters');

  if (loading) return <p className="text-gray-500 text-sm">Loading...</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Masters</h2>
          <p className="text-sm text-gray-500 mt-1">Manage reference data used across tasks</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('masters')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'masters' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          🗂️ Reference Data
        </button>
        <button
          onClick={() => setActiveTab('complianceTypes')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'complianceTypes' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          📅 Compliance Types
        </button>
      </div>

      {activeTab === 'masters' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {sections.map((section) => (
              <MasterTable
                key={section.type}
                section={section}
                onAdd={handleAdd}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
            <CategoryTable items={categories} onRefresh={fetchAll} />
          </div>
          <BillingEntityTable items={billingEntities} onRefresh={fetchAll} />
        </div>
      )}

      {activeTab === 'complianceTypes' && (
        <ComplianceTypeTable items={complianceTypesData} onRefresh={fetchAll} />
      )}
    </div>
  );
};

export default Masters;
